import { useEffect, useRef } from 'react'

// Brand palette orb configs — size is diameter, speed is px per rendered frame
const ORBS = [
  { size: 460, color: 'rgba(201,211,58,0.22)',  speed: 1.82 },  // pyramid-1  yellow-green
  { size: 380, color: 'rgba(248,129,22,0.20)',  speed: 1.50 },  // pyramid-10 orange
  { size: 320, color: 'rgba(25,162,207,0.18)',  speed: 2.14 },  // pyramid-19 cyan
]

const HeroCanvas = () => {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    let rafId  = null
    let frame  = 0
    let orbs   = []

    const init = () => {
      canvas.width  = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
      const w = canvas.width
      const h = canvas.height

      orbs = ORBS.map(({ size, color, speed }) => {
        const r    = size / 2
        // Random starting fractions so orbs begin spread across the canvas
        const xFrac = 0.2 + Math.random() * 0.6
        const yFrac = 0.2 + Math.random() * 0.6
        const dirX  = xFrac < 0.5 ? 1 : -1
        const dirY  = yFrac < 0.5 ? 1 : -1
        return {
          r,
          color,
          x:  Math.max(r, Math.min(w - r, xFrac * w)),
          y:  Math.max(r, Math.min(h - r, yFrac * h)),
          vx: dirX * speed * (0.9 + 0.2 * Math.random()),
          vy: dirY * speed * (0.9 + 0.2 * Math.random()),
        }
      })
    }

    const drawOrb = (orb) => {
      const grad = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.r)
      // Inner stop: use the orb's color as-is (already low alpha)
      grad.addColorStop(0,   orb.color)
      grad.addColorStop(1,   orb.color.replace(/[\d.]+\)$/, '0)'))
      ctx.beginPath()
      ctx.arc(orb.x, orb.y, orb.r, 0, Math.PI * 2)
      ctx.fillStyle = grad
      ctx.fill()
    }

    const loop = () => {
      rafId = requestAnimationFrame(loop)

      // Render every other frame — matches the why-us orbs cadence
      if (++frame % 2 !== 0) return

      const w = canvas.width
      const h = canvas.height

      ctx.clearRect(0, 0, w, h)

      // Update positions + wall bounce
      for (const orb of orbs) {
        orb.x += orb.vx
        orb.y += orb.vy

        if (orb.x - orb.r < 0)  { orb.x = orb.r;      orb.vx =  Math.abs(orb.vx) }
        if (orb.x + orb.r > w)  { orb.x = w - orb.r;  orb.vx = -Math.abs(orb.vx) }
        if (orb.y - orb.r < 0)  { orb.y = orb.r;      orb.vy =  Math.abs(orb.vy) }
        if (orb.y + orb.r > h)  { orb.y = h - orb.r;  orb.vy = -Math.abs(orb.vy) }
      }

      // Elastic orb-to-orb collision
      for (let i = 0; i < orbs.length; i++) {
        for (let j = i + 1; j < orbs.length; j++) {
          const a  = orbs[i]
          const b  = orbs[j]
          const dx = b.x - a.x
          const dy = b.y - a.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          const minD = a.r + b.r

          if (dist < minD && dist > 0.1) {
            const nx  = dx / dist
            const ny  = dy / dist
            const dot = (a.vx - b.vx) * nx + (a.vy - b.vy) * ny

            if (dot > 0) {
              a.vx -= dot * nx
              a.vy -= dot * ny
              b.vx += dot * nx
              b.vy += dot * ny
            }

            const overlap = (minD - dist) * 0.51
            a.x -= nx * overlap
            a.y -= ny * overlap
            b.x += nx * overlap
            b.y += ny * overlap
          }
        }
      }

      // Draw
      for (const orb of orbs) drawOrb(orb)
    }

    init()
    loop()

    const onResize = () => { init() }
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ display: 'block' }}
    />
  )
}

export default HeroCanvas
